"use server";

import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { auth } from "@/auth";
import { db } from "@/lib/db";
import { validateCheckoutConsent } from "@/lib/consent";
import { createCheckout } from "@/lib/payments";
import { checkRateLimit } from "@/lib/rate-limit";

export async function checkoutTour(
  tourId: string,
  _prevState: string | undefined,
  formData: FormData,
) {
  const tour = await db.tour.findUnique({ where: { id: tourId } });
  if (!tour || !tour.published || tour.kind !== "ready") {
    return "Ta wycieczka nie jest obecnie dostępna.";
  }

  const session = await auth();
  if (!session?.user) {
    redirect(`/login?callbackUrl=${encodeURIComponent(`/tours/${tour.slug}`)}`);
  }

  const consentError = validateCheckoutConsent(formData);
  if (consentError) return consentError;

  if (!checkRateLimit(`checkout:${session.user.id}`, 10, 10 * 60 * 1000)) {
    return "Zbyt wiele prób zakupu. Spróbuj ponownie za chwilę.";
  }

  const alreadyOwned = await db.orderItem.findFirst({
    where: {
      itemType: "tour",
      itemId: tour.id,
      order: { userId: session.user.id, status: "paid" },
    },
  });
  if (alreadyOwned) {
    return "Masz już ten plan podróży na swoim koncie - sprawdź sekcję „Moje konto”.";
  }

  const order = await db.order.create({
    data: {
      userId: session.user.id,
      status: "pending",
      total: tour.price,
      currency: "PLN",
      consentAcceptedAt: new Date(),
      items: {
        create: {
          itemType: "tour",
          itemId: tour.id,
          titleSnapshot: tour.title,
          priceSnapshot: tour.price,
        },
      },
    },
  });

  const headersList = await headers();
  const host = headersList.get("host");
  const protocol = host?.startsWith("localhost") || host?.startsWith("127.0.0.1") ? "http" : "https";
  const baseUrl = `${protocol}://${host}`;

  let checkoutSession;
  try {
    checkoutSession = await createCheckout({
      orderId: order.id,
      currency: "PLN",
      items: [{ title: tour.title, quantity: 1, unitAmount: tour.price }],
      customerEmail: session.user.email ?? undefined,
      successUrl: `${baseUrl}/account?checkout=success`,
      cancelUrl: `${baseUrl}/tours/${tour.slug}?checkout=canceled`,
    });
  } catch (error) {
    console.error("checkoutTour: createCheckout failed", error);
    return "Nie udało się rozpocząć płatności. Spróbuj ponownie.";
  }

  await db.order.update({
    where: { id: order.id },
    data: { provider: "stripe", providerRef: checkoutSession.providerRef },
  });

  redirect(checkoutSession.url);
}
